import { GraphQLClient, RequestMiddleware } from 'graphql-request';
import type { JwtPayload } from 'jwt-decode';
import jwtDecode from 'jwt-decode';
import Cookies from 'universal-cookie';
import {
  Authentication,
  ElementsApp,
  useApiUrl,
  useElementsEnvironmentContext,
} from '../../environment';
import { useGraphqlClientContext } from '../context';
import { KnownElementHeader } from '../types';
import { spreadKnownElementsHeaders } from '../util';
import { refreshTokens } from '../auth';

export const REFRESH_THRESHOLD = 60 * 5; // 5 minutes left before token expires

const authCookieNames: Record<ElementsApp, { accessToken: string; refreshToken: string }> = {
  advisor: { accessToken: '_adv_accessToken', refreshToken: '_adv_refreshToken' },
  client: { accessToken: '__accessToken', refreshToken: '__refreshToken' },
  admin: { accessToken: '_adm_accessToken', refreshToken: '_adm_refreshToken' },
};

/**
 * Resolve the current access + refresh tokens, preferring the in-memory
 * `authentication` value provided by ElementsEnvironmentContext over any
 * cookie a prior client session may have left on the device.
 *
 * ELE-929: mobile-web webviews receive fresh tokens as URL query params on
 * every load. Before this change the middleware read only cookies, so the
 * first render wave could fire GraphQL requests with a stale cookie (or no
 * cookie at all) even though the current session's tokens were already
 * available in memory.
 */
const resolveTokens = (
  app: ElementsApp,
  authentication: Authentication | undefined | null
): { accessToken: string; refreshToken: string } => {
  const cookies = new Cookies();

  const inMemoryAccess = authentication?.accessToken ?? '';
  const inMemoryRefresh = authentication?.refreshToken ?? '';

  let cookieAccess = '';
  let cookieRefresh = '';
  // Guard access and refresh token decoding independently so a malformed
  // access cookie does not prevent a valid refresh cookie from being parsed.
  try {
    const accessTokenCookie = cookies.get(authCookieNames[app].accessToken);
    if (accessTokenCookie) {
      cookieAccess = JSON.parse(atob(accessTokenCookie))['access_token'] ?? '';
    }
  } catch {
    // Malformed access cookie — treat as absent. In-memory value still wins.
  }
  try {
    const refreshTokenCookie = cookies.get(authCookieNames[app].refreshToken);
    if (refreshTokenCookie) {
      cookieRefresh = JSON.parse(atob(refreshTokenCookie))['refresh_token'] ?? '';
    }
  } catch {
    // Malformed refresh cookie — treat as absent. In-memory value still wins.
  }

  return {
    accessToken: inMemoryAccess || cookieAccess,
    refreshToken: inMemoryRefresh || cookieRefresh,
  };
};

const writeAuthCookies = (
  app: ElementsApp,
  apiUrl: string,
  tokens: { accessToken: string; refreshToken: string }
) => {
  const cookies = new Cookies();
  cookies.set(
    authCookieNames[app].accessToken,
    btoa(JSON.stringify({ access_token: tokens.accessToken })),
    {
      path: '/',
      sameSite: 'lax',
      httpOnly: false,
      secure: apiUrl.indexOf('.dev') === -1,
    }
  );
  cookies.set(
    authCookieNames[app].refreshToken,
    btoa(JSON.stringify({ refresh_token: tokens.refreshToken })),
    {
      path: '/',
      sameSite: 'lax',
      httpOnly: false,
      secure: apiUrl.indexOf('.dev') === -1,
    }
  );
};

const safeDecodeExp = (token: string): number | null => {
  try {
    return jwtDecode<JwtPayload>(token).exp ?? null;
  } catch {
    return null;
  }
};

export const getRequestMiddleware = (
  apiUrl: string,
  app: ElementsApp,
  getAuthentication: () => Authentication | undefined | null
): RequestMiddleware => {
  // Refreshed token pair, cached across requests within this middleware
  // instance. Without this, after a refresh the upstream in-memory value
  // returned by `getAuthentication()` is still the expired access token
  // (the ElementsEnvironmentContext is populated once from URL params and
  // does not observe our refresh). `resolveTokens` would then keep picking
  // the expired token on every subsequent request, causing repeated refresh
  // attempts — and, if the refresh token also rotated, outright auth
  // failure. Reset when the upstream access token changes so a fresh
  // URL-driven session rotation always wins.
  let refreshedFor: string | null = null;
  let refreshedTokens: { accessToken: string; refreshToken: string } | null = null;
  // In-flight refresh promise keyed by the upstream access token that
  // triggered it. Concurrent requests using the same expired token share
  // one refresh call instead of racing N refresh requests against the API
  // (and against each other's rotated refresh tokens).
  let inflightRefreshFor: string | null = null;
  let inflightRefresh: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

  return async (request) => {
    const upstream = resolveTokens(app, getAuthentication());

    if (refreshedFor !== null && upstream.accessToken !== refreshedFor) {
      refreshedFor = null;
      refreshedTokens = null;
    }

    const accessToken = refreshedTokens?.accessToken || upstream.accessToken;
    const refreshToken = refreshedTokens?.refreshToken || upstream.refreshToken;

    // If we have no access token from either source, send the request
    // unauthenticated (matches previous behavior).
    if (!accessToken) {
      return request;
    }

    // A cookie can round-trip through base64/JSON without producing a valid
    // JWT (corrupted prior-session cookie, wrong app writing to the same
    // key, etc). Treat an undecodable access token as absent so we do not
    // throw before the refresh path has a chance to run.
    const expiresAt = safeDecodeExp(accessToken);
    if (expiresAt === null && !refreshToken) {
      return request;
    }
    const shouldRefresh =
      expiresAt === null || (expiresAt - REFRESH_THRESHOLD) * 1000 < Date.now();

    if (shouldRefresh && refreshToken) {
      // Coalesce concurrent refreshes for the same upstream token.
      if (inflightRefreshFor !== upstream.accessToken || inflightRefresh === null) {
        // Capture the upstream token at the start of the refresh so we can verify
        // it hasn't changed (fresh URL-driven session rotation) before storing the
        // refreshed result.
        const upstreamAccessToken = upstream.accessToken;
        inflightRefreshFor = upstreamAccessToken;
        inflightRefresh = (async () => {
          const insecureClient = new GraphQLClient(apiUrl);
          const { tokenSet } = await refreshTokens(insecureClient, { refreshToken });
          if (!tokenSet) return null;
          // Only store the refreshed pair if the upstream access token hasn't
          // changed. If it has, discard the refresh result and let the next
          // request handle the new upstream token.
          if (upstreamAccessToken !== resolveTokens(app, getAuthentication()).accessToken) {
            return null;
          }
          writeAuthCookies(app, apiUrl, {
            accessToken: tokenSet.accessToken,
            refreshToken: tokenSet.refreshToken,
          });
          refreshedFor = upstreamAccessToken;
          refreshedTokens = {
            accessToken: tokenSet.accessToken,
            refreshToken: tokenSet.refreshToken,
          };
          return refreshedTokens;
        })().finally(() => {
          if (inflightRefreshFor === upstreamAccessToken) {
            inflightRefreshFor = null;
            inflightRefresh = null;
          }
        });
      }

      const refreshed = await inflightRefresh;
      if (!refreshed) {
        console.error('Token refresh failed');
        return request;
      }

      return {
        ...request,
        headers: {
          ...request.headers,
          Authorization: `Bearer ${refreshed.accessToken}`,
        },
      };
    }

    return {
      ...request,
      headers: {
        ...request.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    };
  };
};

export const useGraphqlClient = (knownElementHeaders: KnownElementHeader[] = []) => {
  const apiUrl = useApiUrl();
  const { app, authentication } = useElementsEnvironmentContext();
  const gqlClientContext = useGraphqlClientContext();

  return (
    gqlClientContext?.gqlClient ??
    new GraphQLClient(apiUrl, {
      headers: spreadKnownElementsHeaders({}, knownElementHeaders),
      // Pass a getter so the middleware sees the latest authentication value
      // on every request rather than the snapshot captured at client
      // construction time.
      requestMiddleware: getRequestMiddleware(apiUrl, app, () => authentication),
    })
  );
};
